import axios from "axios";
import { refactorEventsGroupedByDateV1 } from "@/services/eventsOddsHandlerV3";

const COOLDOWN_MS = 180000; // 3 minutes cooldown to prevent API spam from the 30s UI timer

const state = {
    events: [],
    sports: [],
    paging: {
        total: 0,
        page: 1,
        pages: 1
    },
    loading: true,
    lastFetchMap: {}, // Tracks timestamps for cooldowns: { "sportId_page": timestamp }

    // --- NUOVO STATO PER LATEST WINS ---
    latestWins: [],
    loadingLatestWins: false
};

const getters = {
    getBestPlayedEvents: (state) => state.events,
    getBestPlayedSports: (state) => state.sports,
    getBestPlayedPaging: (state) => state.paging,
    isLoadingBestPlayed: (state) => state.loading,

    // --- NUOVI GETTERS PER LATEST WINS ---
    getLatestWins: (state) => state.latestWins,
    isLoadingLatestWins: (state) => state.loadingLatestWins
};

const mutations = {
    SET_LOADING(state, status) {
        state.loading = status;
    },

    SET_DATA(state, { events, paging, sports }) {
        state.events = events;

        if (paging) state.paging = paging;
        if (sports) state.sports = sports;
    },

    SET_LAST_FETCH(state, { key, time }) {
        // Assigning a new object ensures reactivity if ever needed
        state.lastFetchMap = { ...state.lastFetchMap, [key]: time };
    },

    RESET_DATA(state) {
        state.events = [];
        state.paging = { total: 0, page: 1, pages: 1 };
        // We do NOT reset sports or lastFetchMap here to maintain UI stability and prevent error loops
    },

    // --- NUOVE MUTATIONS PER LATEST WINS ---
    SET_LATEST_WINS(state, wins) {
        state.latestWins = wins;
    },

    SET_LOADING_LATEST_WINS(state, status) {
        state.loadingLatestWins = status;
    }
};

const actions = {
    async fetchEventBestPlayedV4(context, payload) {
        if (context.rootGetters['ui/getAppSection'] === 'live') return;
        const p = payload || {};

        // Cooldown Logic Variables
        const force = p.force || false;
        const sportId = p.sportId || null;
        const page = p.page || 1;
        const cacheKey = `${sportId}_${page}`;
        const now = Date.now();

        // Check if we should block the call based on the cooldown
        if (!force && context.state.lastFetchMap[cacheKey]) {
            if (now - context.state.lastFetchMap[cacheKey] < COOLDOWN_MS) {
                return; // Stop execution here
            }
        }

        context.commit('SET_LOADING', true);

        const requestPayload = {
            platformId: context.rootGetters['settings/getPlatformId'],
            sportId: sportId,
            page: page,
            limit: p.limit || context.rootGetters['settings/getMostPlayedMaxEventsForPage'] || 10,
            language_user: p.language_user,
            mobilePlatform: p.mobilePlatform || false
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'];
        const token = context.rootGetters['settings/getToken'];
        const url = `${apiUrl}/v3/events/prematch/popular`;


        const headers = {
            Authorization: `Bearer ${token}`
        };

        try {
            const response = await axios.post(url, requestPayload, { headers });
            const data = response.data;

            // Update the fetch timestamp on successful response
            context.commit('SET_LAST_FETCH', { key: cacheKey, time: Date.now() });

            if (data && data.result && data.result.events && data.result.events.length > 0) {

                const formattedEvents = refactorEventsGroupedByDateV1(data.result);

                context.commit('SET_DATA', {
                    events: formattedEvents,
                    paging: data.result.paging,
                    sports: data.result.sports
                });

                if (data.result.headerSign) {
                    context.dispatch(
                        'marketview/updateSpreadsListAndSpreadSelectedV3',
                        {
                            type: 'bestPlayed',
                            headerSign: data.result.headerSign,
                            sport: requestPayload.sportId,
                            events: data.result.events
                        },
                        { root: true }
                    );
                }

            } else {
                context.commit('RESET_DATA');
            }

        } catch (error) {
            console.error("Error in fetchEventBestPlayedV4:", error);
            context.commit('RESET_DATA');
            context.commit('SET_LAST_FETCH', { key: cacheKey, time: Date.now() });
        } finally {
            context.commit('SET_LOADING', false);
        }
    },


    async fetchLatestWins(context, payload = {}) {
        context.commit('SET_LOADING_LATEST_WINS', true);

        const requestPayload = {
            platform_id: payload.platform_id || context.rootGetters['settings/getPlatformId'],
            limit: payload.limit || 6
        };

        const apiUrl = context.rootGetters['settings/getUrlApi'] || context.rootGetters['settings/getUrl'];
        const token = context.rootGetters['settings/getToken'] || context.rootGetters['profile/getToken'];

        const url = `${apiUrl}/reports/latest-wins`;

        const headers = {
            Authorization: `Bearer ${token}`
        };

        try {
            const response = await axios.post(url, requestPayload, { headers });

            if (response.data && response.data.data) {
                context.commit('SET_LATEST_WINS', response.data.data);
            } else {
                context.commit('SET_LATEST_WINS', []);
            }
        } catch (error) {
            console.error("Error in fetchLatestWins:", error);
            context.commit('SET_LATEST_WINS', []);
        } finally {
            context.commit('SET_LOADING_LATEST_WINS', false);
        }
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};